// Pure mapping from Firebase Auth `FirebaseError.code` to Italian copy
// shown to the user.
//
// Why a pure helper
// -----------------
// Keeps the LoginPage rendering deterministic: the page receives an
// `unknown` from the mutation and the helper resolves it to a stable
// string. Easy to extend without touching the page, and easy to
// reason about when reading the Italian copy in isolation.
//
// User-existence leak guard
// -------------------------
// The four "wrong creds" Firebase error codes
//   - auth/invalid-credential
//   - auth/user-not-found
//   - auth/wrong-password
//   - auth/invalid-login-credentials   (newer SDK alias for the same condition)
// all collapse to the SAME message. A login form must NEVER let a
// caller distinguish "email exists, wrong password" from "email does
// not exist" — that is a free email-enumeration oracle for any
// attacker. Adding a new code here is a security-relevant change.
//
// Unknown codes fall through to the generic copy rather than echoing
// the raw code so we don't accidentally leak Firebase internals.

const SAME_AS_INVALID_CREDENTIALS = 'Credenziali non valide.';

const FIREBASE_ERROR_COPY: Record<string, string> = {
  'auth/invalid-credential': SAME_AS_INVALID_CREDENTIALS,
  'auth/invalid-login-credentials': SAME_AS_INVALID_CREDENTIALS,
  'auth/user-not-found': SAME_AS_INVALID_CREDENTIALS,
  'auth/wrong-password': SAME_AS_INVALID_CREDENTIALS,
  'auth/user-disabled': 'Account disabilitato. Contatta Aurino Broker.',
  'auth/too-many-requests':
    'Troppi tentativi. Riprova più tardi.',
  'auth/network-request-failed':
    'Errore di rete. Controlla la connessione e riprova.',
};

const DEFAULT_COPY = 'Accesso non riuscito. Riprova.';

/**
 * Extract a Firebase error code from an `unknown` and resolve it to
 * Italian copy. Tolerates both the v9+ `FirebaseError` shape (with
 * `code` property) and any `Error` whose `code` happens to be a
 * string. Anything else returns the generic copy.
 */
export function mapFirebaseAuthError(err: unknown): string {
  const code = extractCode(err);
  if (!code) return DEFAULT_COPY;
  return FIREBASE_ERROR_COPY[code] ?? DEFAULT_COPY;
}

function extractCode(err: unknown): string | null {
  if (err === null || typeof err !== 'object') return null;
  const candidate = (err as { code?: unknown }).code;
  return typeof candidate === 'string' && candidate.length > 0
    ? candidate
    : null;
}
